import React, { useContext } from 'react'
import { Route, Redirect } from 'react-router-dom'
import AuthContext from '@/product/context/auth/authContext'
import InitialPrivateFunction from '@/product/views/InitialFunction/private'
import Loaders from '@/product/components/molecules/Loaders'
import { SIDE_PANEL } from '@/utils/UI'

const exemptedRoutes = ['/dashboard', '/change_password']

// global.strictPermissionGroups (App.js): list pages inside these groups need their exact permission.
function isStrictPath(pathname, user, permissionGroup) {
    return (
        !permissionGroup &&
        Array.isArray(global.strictPermissionGroups) &&
        user?.emp_type !== 'super_admin' &&
        global.strictPermissionGroups.some((group) =>
            pathname.startsWith(`${group}/`)
        )
    )
}

function hasRoutePermission(
    pathname,
    permissions,
    permissionGroup,
    strict,
    strictDetails = false
) {
    const permissionList = Array.isArray(permissions)
        ? permissions
        : typeof permissions === 'string'
        ? permissions.split(',')
        : []

    return permissionList.some((permission) => {
        if (typeof permission !== 'string') {
            return false
        }

        const permittedPath = permission.trim().replace(/\/+$/, '')
        if (!permittedPath) {
            return false
        }

        if (strict) {
            return pathname === permittedPath
        }

        if (strictDetails) {
            const prefix = `${permissionGroup}/`
            return (
                permittedPath.startsWith(prefix) &&
                SIDE_PANEL.some(
                    (section) =>
                        `/${section.path.replace(/^\/+/, '')}` ===
                            permissionGroup &&
                        section.subLinks?.some(
                            (link) => link.path === permittedPath
                        )
                )
            )
        }

        return (
            pathname === permittedPath ||
            pathname.startsWith(`${permittedPath}/`) ||
            (permissionGroup &&
                (permittedPath === permissionGroup ||
                    permittedPath.startsWith(`${permissionGroup}/`)))
        )
    })
}

// Same check PrivateRoute applies, for code that redirects programmatically.
export function canAccessPath(pathname, user) {
    const path = `${pathname || ''}`.replace(/\/+$/, '') || '/'
    return (
        exemptedRoutes.includes(path) ||
        hasRoutePermission(
            path,
            user?.emp_permissions,
            undefined,
            isStrictPath(path, user)
        )
    )
}

function PrivateRoute({
    component: Component,
    children,
    permissionGroup,
    ...rest
}) {
    const authContext = useContext(AuthContext)
    const { user, loading, isAuthenticated } = authContext

    return (
        <Route
            {...rest}
            render={(props) => {
                if (loading) {
                    return <Loaders isLoading={true} />
                }

                if (!isAuthenticated) {
                    return (
                        <Redirect
                            to={{
                                pathname: '/',
                                state: { referer: props.location },
                            }}
                        />
                    )
                }

                const pathname =
                    props.location.pathname.replace(/\/+$/, '') || '/'
                const hasAccess =
                    exemptedRoutes.includes(pathname) ||
                    hasRoutePermission(
                        pathname,
                        user?.emp_permissions,
                        permissionGroup,
                        isStrictPath(pathname, user, permissionGroup),
                        permissionGroup &&
                            isStrictPath(`${permissionGroup}/`, user)
                    )

                if (!hasAccess) {
                    return (
                        <Redirect
                            to={{
                                pathname: '/dashboard',
                                state: { referer: props.location },
                            }}
                        />
                    )
                }

                return (
                    <>
                        <InitialPrivateFunction
                            fields={true}
                            tables={true}
                            pageFields={true}
                            dashboard={true}
                        />
                        {Component ? <Component {...props} /> : children}
                    </>
                )
            }}
        />
    )
}

export default PrivateRoute
